import { api } from "../api";

export type BranchStatus = "ACTIVE" | "INACTIVE";

export interface BranchInventoryItem {
  id: string;
  barcode: string;
  accessionNumber: number;
  copyNumber: number;
  statusCode: string;
  conditionCode?: string | null;
  biblio?: { id: string; title: string; authors?: string[] | null; isbn?: string | null };
  itemType?: { name: string };
  location?: { name: string };
  collection?: { name: string };
}

export interface Branch {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  managerId?: string | null;
  isMain: boolean;
  status: BranchStatus;
  permissions: string[];
  settings?: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    staff: number;
  };
}

export interface CreateBranchDto {
  name: string;
  code: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
  managerId?: string;
  isMain?: boolean;
  status?: BranchStatus;
  permissions?: string[];
}

export interface UpdateBranchDto {
  name?: string;
  code?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
  managerId?: string;
  isMain?: boolean;
  status?: BranchStatus;
  settings?: Record<string, any>;
}

export interface UpdateBranchPermissionsDto {
  permissions: string[];
}

export const branchesApi = {
  getAll: async (includeInactive = false): Promise<Branch[]> => {
    const response = await api.get("/branches", {
      params: { includeInactive },
    });
    return response.data;
  },

  getActiveBranches: async (): Promise<Branch[]> => {
    const response = await api.get("/branches", { params: { includeInactive: false } });
    return response.data;
  },

  getById: async (id: string): Promise<Branch> => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },

  getItems: async (branchId: string): Promise<BranchInventoryItem[]> => {
    const response = await api.get(`/branches/${branchId}/items`);
    return response.data;
  },

  create: async (data: CreateBranchDto): Promise<Branch> => {
    const response = await api.post("/branches", data);
    return response.data;
  },

  update: async (id: string, data: UpdateBranchDto): Promise<Branch> => {
    const response = await api.patch(`/branches/${id}`, data);
    return response.data;
  },

  updatePermissions: async (id: string, data: UpdateBranchPermissionsDto): Promise<Branch> => {
    const response = await api.patch(`/branches/${id}/permissions`, data);
    return response.data;
  },

  getPermissions: async (id: string): Promise<{ permissions: string[] }> => {
    const response = await api.get(`/branches/${id}/permissions`);
    return response.data;
  },

  /**
   * Two-stage lifecycle:
   * - ACTIVE branch → deactivates (soft-delete, returns { status: "INACTIVE" })
   * - INACTIVE branch → permanently deletes if no blocking records exist
   *   On failure returns 400 with { message, blockers: string[], hint }
   */
  delete: async (
    id: string
  ): Promise<{ id: string; name: string; status?: string; deleted?: boolean }> => {
    const response = await api.delete(`/branches/${id}`);
    return response.data;
  },

  reactivate: async (id: string): Promise<Branch> => {
    const response = await api.patch(`/branches/${id}/reactivate`);
    return response.data;
  },
};
